import React, { createContext, useState, useEffect } from "react";
import { API_BASE_URL } from "../config/apiKey.js";

export const ChatContext = createContext();

// 🎯 Generate meaningful title from message content
const generateChatTitle = (content) => {
  if (!content || content.trim().length === 0) return "New Chat";

  // Remove common prefixes and clean up
  const cleanContent = content
    .replace(/^(help|how|what|why|when|where|can|please|hey|hi|hello)\s+/i, "")
    .trim();

  // Take first 4-6 words for title
  const words = cleanContent.split(/\s+/).slice(0, 6);
  let title = words.join(" ");

  // Add ellipsis if original content was longer
  if (content.split(/\s+/).length > 6) {
    title += "...";
  }

  // Capitalize first letter
  title = title.charAt(0).toUpperCase() + title.slice(1);

  return title;
};

export const ChatProvider = ({ children }) => {
  // Helper to get auth token from localStorage (works without AuthProvider)
  const getAuthToken = () => {
    return localStorage.getItem("auth_token") || null;
  };

  // Helper for safe localStorage
  const safeLoad = (key) => {
    try {
      const val = localStorage.getItem(key);
      return val ? JSON.parse(val) : [];
    } catch {
      localStorage.removeItem(key);
      return [];
    }
  };

  const [projects, setProjects] = useState(() => []);
  const [chats, setChats] = useState(() => []);

  // Initialize currentChat and messages - fresh start on login
  const [currentChat, setCurrentChat] = useState(() => null);

  const [currentProject, setCurrentProject] = useState(() => null);

  const [messages, setMessages] = useState(() => []);

  // Selected model / version (for header + chat bar)
  const [selectedModel, setSelectedModel] = useState(
    () => localStorage.getItem("exivis_selectedModel") || "gpt-4o-mini"
  );

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);

  // 🧠 NEW: processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  const persist = (key, data) => localStorage.setItem(key, JSON.stringify(data));

  // 🔒 Ensure message content is always a string (prevents React error #130)
  const sanitizeMessageContent = (content) => {
    if (typeof content === "string") {
      return content;
    }
    if (content === null || content === undefined) {
      return "";
    }
    if (typeof content === "number" || typeof content === "boolean") {
      return String(content);
    }
    if (typeof content === "object") {
      // Try to extract meaningful string from object
      if (content.message && typeof content.message === "string") {
        return content.message;
      }
      if (content.error && typeof content.error === "string") {
        return content.error;
      }
      if (content.text && typeof content.text === "string") {
        return content.text;
      }
      if (content.content && typeof content.content === "string") {
        return content.content;
      }
      // Last resort: JSON stringify
      try {
        return JSON.stringify(content);
      } catch {
        return "";
      }
    }
    return String(content || "");
  };

  // 🆕 Create Project
  const createProject = ({ name, desc }) => {
    const project = {
      id: Date.now(),
      name,
      description: desc || "",
      createdAt: new Date().toISOString(),
    };
    const updated = [...projects, project];
    setProjects(updated);
    persist("exivis_projects", updated);
    setCurrentProject(project.id);
    return project;
  };

  // 🆕 Start New Chat - Don't create chat on backend until first message is sent
  // This prevents empty chats from being saved
  const startNewChat = async (projectId = null, model = null) => {
    const effectiveProjectId = projectId || currentProject;

    try {
      // Don't create chat on backend yet - wait for first message
      // Create a temporary local chat that will be saved when first message is sent
      const tempChatId = Date.now();
      const newChat = {
        id: tempChatId,
        title: "New Chat",
        messages: [],
        projectId: effectiveProjectId,
        createdAt: new Date().toISOString(),
        is_pinned: false,
        is_archived: false,
        isTemporary: true, // Mark as temporary until first message
      };

      const updated = [...chats, newChat];
      setChats(updated);
      setCurrentChat(newChat.id);
      setMessages([]);
      persist("exivis_chats", updated);

      // Save current chat to localStorage
      localStorage.setItem("exivis_currentChat", String(newChat.id));
      if (effectiveProjectId) {
        localStorage.setItem("exivis_currentProject", String(effectiveProjectId));
      }

      // Ensure current project is set when creating chat in a project
      if (effectiveProjectId && !currentProject) {
        setCurrentProject(effectiveProjectId);
      }

      return newChat.id;
    } catch (error) {
      console.error("Error creating chat:", error);
      // Fallback to local creation if backend fails
      const newChat = {
        id: Date.now(),
        title: "New Chat",
        messages: [],
        projectId: effectiveProjectId,
        createdAt: new Date().toISOString(),
        is_pinned: false,
        is_archived: false,
        isTemporary: true,
      };

      const updated = [...chats, newChat];
      setChats(updated);
      setCurrentChat(newChat.id);
      setMessages([]);
      persist("exivis_chats", updated);
      return newChat.id;
    }
  };

  // ➕ Add Message + show processing text
  const addMessage = async (msg) => {
    console.log("💬 addMessage called with:", msg);
    // Prevent duplicate requests - check isProcessing at the very start
    if (isProcessing) {
      console.warn("⚠️ Message is already being processed. Ignoring duplicate request.");
      return;
    }

    if (!msg || (!msg.content?.trim() && (!msg.files || msg.files.length === 0))) {
      console.warn("⚠️ Attempted to add empty message without files");
      return;
    }

    let activeChatId = currentChat;
    if (!activeChatId) {
      activeChatId = await startNewChat(currentProject);
    } else {
      // Check if currentChat exists in chats array, if not, it might be stale/invalid
      const chatExists = chats.some(c => c.id === activeChatId);
      if (!chatExists) {
        console.warn(`⚠️ currentChat ${activeChatId} not found in chats array. Starting new chat.`);
        activeChatId = await startNewChat(currentProject);
      }
    }

    // Create preview URLs for image files
    const filesWithPreviews = (msg.files || []).map(file => {
      if (file.type && file.type.startsWith('image/') && !file.preview) {
        return {
          ...file,
          preview: URL.createObjectURL(file)
        };
      }
      return file;
    });

    const message = {
      role: msg.role || "user",
      content: sanitizeMessageContent(msg.content || ""),
      files: filesWithPreviews,
      timestamp: new Date().toISOString(),
    };

    const updatedMessages = [...messages, message];
    setMessages(updatedMessages);

    let updatedChats = chats.map((chat) =>
      chat.id === activeChatId ? { ...chat, messages: updatedMessages } : chat
    );

    // ✅ FIX: determine temporary chat using updatedChats (not stale `chats`)
    const activeChatObj = updatedChats.find((c) => c.id === activeChatId);
    let isTemporaryChat = !!activeChatObj?.isTemporary;

    // 🪄 Auto-title first message with better naming
    if (activeChatObj && activeChatObj.title === "New Chat" && msg.role === "user") {
      activeChatObj.title = generateChatTitle(msg.content);
    }

    setChats(updatedChats);
    persist("exivis_chats", updatedChats);

    // 🧠 Get AI response from backend API
    if (msg.role === "user") {
      // Set processing state immediately for better UX
      setIsProcessing(true);
      setProcessingMessage("🧠 Processing your request...");

      // 📎 Upload files first if any
      let uploadedFiles = [];

      if (msg.files && msg.files.length > 0) {
        console.log("📎 Files found in message, starting upload. File count:", msg.files.length);
        try {
          setProcessingMessage("📎 Uploading files...");

          // Helper to convert file to base64 (only for images)
          const fileToBase64 = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
          });

          // Upload all files in one go
          const formData = new FormData();
          msg.files.forEach(file => {
            formData.append("files", file);
          });

          const uploadResponse = await fetch(`${API_BASE_URL}/api/upload`, {
            method: "POST",
            headers: {
              ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
              "X-Requested-With": "XMLHttpRequest",
              "Accept": "application/json",
            },
            body: formData,
          });

          console.log("📎 Upload response status:", uploadResponse.status);

          const responseText = await uploadResponse.text();
          const contentType = uploadResponse.headers.get("content-type") || "";
          const isHtml =
            contentType.includes("text/html") ||
            responseText.trim().toLowerCase().startsWith("<!doctype") ||
            responseText.trim().toLowerCase().startsWith("<html");

          if (isHtml) {
            console.error("❌ Upload API returned HTML instead of JSON. This usually means a 404/500 misrouting to the frontend.");
            console.error("Response preview:", responseText.substring(0, 500));
            throw new Error("File upload failed (Server misconfiguration). Please try again or refresh the page.");
          } else if (uploadResponse.ok) {
            try {
              const uploadData = JSON.parse(responseText);
              const filesInfo = uploadData.files || [];
              
              // Map uploaded files and add base64 for images
              uploadedFiles = await Promise.all(filesInfo.map(async (fileInfo, index) => {
                const originalFile = msg.files[index];
                let base64 = null;
                
                if (originalFile.type && originalFile.type.startsWith('image/')) {
                  try {
                    base64 = await fileToBase64(originalFile);
                  } catch (e) {
                    console.error("Error converting to base64:", e);
                  }
                }
                
                return {
                  url: fileInfo.url,
                  type: fileInfo.type || originalFile.type,
                  name: fileInfo.originalName || originalFile.name,
                  base64: base64
                };
              }));
            } catch (parseError) {
              console.error("Failed to parse upload response:", parseError);
              throw new Error("Failed to process server response after upload.");
            }
          } else {
            // Try to extract meaningful error from server
            let serverErrorMsg = '';
            try {
              const errJson = JSON.parse(responseText);
              serverErrorMsg = errJson.message || errJson.error || '';
            } catch (_) {
              // ignore JSON parse error and fall back to generic text
            }

            console.error("File upload failed:", responseText.substring(0, 500));

            if (uploadResponse.status === 413 || serverErrorMsg.toLowerCase().includes('too large')) {
              throw new Error('One or more files are too large. Max size is 10MB per file.');
            }

            throw new Error(serverErrorMsg || `File upload failed with status ${uploadResponse.status}`);
          }
        } catch (uploadError) {
          console.error("Error uploading file:", uploadError);
          setProcessingMessage("");
          setIsProcessing(false);
          // Re-throw to be caught by the main catch block and potentially show in UI
          throw uploadError;
        }
        setProcessingMessage("🧠 Processing your request...");
      }

      // 🎨 Check for image generation request
      if (isImageGenerationRequest(msg.content)) {
        console.log("🎨 Image generation detected for:", msg.content);
        setProcessingMessage("🎨 Generating your image...");

        let attempts = 0;
        const maxAttempts = 2;
        let success = false;

        while (attempts < maxAttempts && !success) {
          attempts++;
          try {
            // Add cache busting to prevent CloudFront from serving index.html
            const fetchUrl = `${API_BASE_URL}/api/images/generate`;

            const imageResponse = await fetch(fetchUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
                "X-Requested-With": "XMLHttpRequest",
                "Accept": "application/json",
              },
              body: JSON.stringify({ prompt: msg.content }),
            });

            // Get response text first to check if it's HTML or JSON
            const responseText = await imageResponse.text();
            const contentType = imageResponse.headers.get("content-type") || "";
            const isHtml =
              contentType.includes("text/html") ||
              responseText.trim().toLowerCase().startsWith("<!doctype") ||
              responseText.trim().toLowerCase().startsWith("<html");


            success = true;

            if (!imageResponse.ok) {
              let errorData = {};

              // Try to parse as JSON first (even if it looks like HTML, sometimes error responses can be mixed)
              if (!isHtml) {
                try {
                  errorData = JSON.parse(responseText);
                } catch (e) {
                  console.error("Failed to parse error response as JSON:", responseText.substring(0, 200));
                  errorData = {
                    error: "SERVER_ERROR",
                    message: `Server returned ${imageResponse.status} error. Please try again later.`
                  };
                }
              } else {
                // If it's HTML, it's a routing issue, but check status code first
                if (imageResponse.status === 403) {
                  // 403 might be a limit error even if wrapped in HTML
                  console.error("❌ Image API returned HTML with 403 status. This might be a limit error.");
                  // Try to show limit message anyway
                  const errorMsg = {
                    role: "assistant",
                    content: sanitizeMessageContent("I apologize, but your daily image generation limit is over for today. We politely suggest upgrading your plan to continue or checking back tomorrow when your limit resets. Thank you! 😊"),
                    timestamp: new Date().toISOString(),
                  };
                  setMessages([...updatedMessages, errorMsg]);
                  setIsProcessing(false);
                  setProcessingMessage("");
                  return;
                }
                // For other HTML responses, show service unavailable
                console.error("❌ Image API returned HTML instead of JSON. API endpoint may not be accessible.");
                console.error("Response preview:", responseText.substring(0, 500));
                setIsProcessing(false);
                setProcessingMessage("");
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent("⚠️ **Service Unavailable**\n\nImage generation service is temporarily unavailable. The server may be experiencing issues. Please try again in a few moments.\n\nIf this problem persists, please contact support."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                return;
              }

              // Handle JSON error responses
              if (errorData.error === "PLAN_NOT_SUPPORTED") {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent(errorData.message || `⚠️ Image generation requires an upgrade. Free tier allows 1 image per day. [Upgrade here](/pricing)`),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                return;
              }

              if (errorData.error === "DAILY_LIMIT_REACHED" || errorData.error === "FREE_LIMIT_REACHED") {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent("I apologize, but your daily image generation limit is over for today. We politely suggest upgrading your plan to continue or checking back tomorrow when your limit resets. Thank you! 😊"),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                return;
              }
              if (errorData.error === "CONTENT_POLICY_VIOLATION" || imageResponse.status === 400) {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent(`⚠️ The prompt "${sanitizeMessageContent(msg.content)}" violates content policy. Please try a different, more appropriate prompt. For example, instead of "man eating", try "person enjoying a meal" or "dining scene".`),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                return;
              }
              throw new Error(errorData.error || errorData.message || "Image generation failed");
            }

            if (isHtml) {
              console.error("❌ Image API returned HTML instead of JSON. API endpoint may not be accessible.");
              console.error("Status:", imageResponse.status);
              console.error("URL:", imageResponse.url);
              console.error("Response preview:", responseText.substring(0, 500));

              // If we got HTML with 200/404 on an API route, it's definitely misrouting
              if (responseText.includes("<!doctype html>") || responseText.includes("<html")) {
                console.warn("⚠️ Misrouting detected for Image API. CloudFront may be serving the frontend for this path.");
              }

              setIsProcessing(false);
              setProcessingMessage("");
              const errorMsg = {
                role: "assistant",
                content: sanitizeMessageContent(`⚠️ **Service Unavailable**\n\nImage generation service is temporarily unavailable. The server may be experiencing issues or the request was misrouted. (Status: ${imageResponse.status})\n\nDetected misrouting: The API request reached the frontend instead of the backend. Please try again in a few moments.`),
                timestamp: new Date().toISOString(),
              };
              setMessages([...updatedMessages, errorMsg]);
              return;
            }

            // Parse response as JSON (HTML error responses are already handled above)
            let imageData;
            try {
              imageData = JSON.parse(responseText);
            } catch (e) {
              console.error("Failed to parse response as JSON:", responseText.substring(0, 200));
              // If we couldn't parse and status is 403, assume it's a limit error
              if (imageResponse.status === 403) {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent("Your daily image generation limit has been reached for today. Your limits will automatically reset tomorrow at midnight."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                return;
              }
              setIsProcessing(false);
              setProcessingMessage("");
              const errorMsg = {
                role: "assistant",
                content: sanitizeMessageContent("⚠️ **Service Error**\n\nServer returned an invalid response. Please try again later."),
                timestamp: new Date().toISOString(),
              };
              setMessages([...updatedMessages, errorMsg]);
              return;
            }

            // Build image message with revised prompt feedback
            // Handle both imageUrl and url formats
            const finalImageUrl = imageData.imageUrl || imageData.url;
            if (!finalImageUrl) {
              console.error("Image response data:", imageData);
              throw new Error("Image URL not found in response. Response: " + JSON.stringify(imageData));
            }

            // Validate that it's a valid URL
            if (typeof finalImageUrl !== 'string' || (!finalImageUrl.startsWith('http://') && !finalImageUrl.startsWith('https://'))) {
              console.error("Invalid image URL format:", finalImageUrl);
              throw new Error("Invalid image URL format received from server");
            }

            let imageContent = `![Generated Image](${finalImageUrl})\n\n`;

            if (imageData.revision) {
              imageContent += `**📝 Revised Prompt:**\n\n`;
              // Ensure revisedPrompt is a string
              let revisedPrompt = '';
              if (typeof imageData.revision.revisedPrompt === 'string') {
                revisedPrompt = imageData.revision.revisedPrompt;
              } else if (imageData.revision.revisedPrompt) {
                // If it's an object, try to extract meaningful text
                if (typeof imageData.revision.revisedPrompt === 'object') {
                  revisedPrompt = imageData.revision.revisedPrompt.text ||
                                 imageData.revision.revisedPrompt.prompt ||
                                 imageData.revision.revisedPrompt.content ||
                                 JSON.stringify(imageData.revision.revisedPrompt, null, 2);
                } else {
                  revisedPrompt = String(imageData.revision.revisedPrompt);
                }
              }
              imageContent += `\`\`\`\n${revisedPrompt}\n\`\`\`\n\n`;

              if (imageData.revision.feedback) {
                // Ensure feedback is a string, not an object
                let feedback = '';
                if (typeof imageData.revision.feedback === 'string') {
                  feedback = imageData.revision.feedback;
                } else if (typeof imageData.revision.feedback === 'object' && imageData.revision.feedback !== null) {
                  // Try to extract meaningful text from object
                  feedback = imageData.revision.feedback.text ||
                            imageData.revision.feedback.message ||
                            imageData.revision.feedback.content ||
                            imageData.revision.feedback.description ||
                            JSON.stringify(imageData.revision.feedback, null, 2);
                } else if (imageData.revision.feedback) {
                  feedback = String(imageData.revision.feedback);
                }

                if (feedback && feedback !== 'null' && feedback !== 'undefined' && feedback.trim().length > 0) {
                  imageContent += `**💡 Feedback:**\n${feedback}\n\n`;
                }
              }

              if (imageData.revision.improvements && Array.isArray(imageData.revision.improvements) && imageData.revision.improvements.length > 0) {
                imageContent += `**✨ Improvements Made:**\n`;
                imageData.revision.improvements.forEach((improvement, idx) => {
                  // Ensure each improvement is a string, not an object
                  let improvementText = '';
                  if (typeof improvement === 'string') {
                    improvementText = improvement;
                  } else if (typeof improvement === 'object' && improvement !== null) {
                    // Try to extract meaningful text from object
                    improvementText = improvement.text ||
                                      improvement.description ||
                                      improvement.message ||
                                      improvement.content ||
                                      JSON.stringify(improvement, null, 2);
                  } else if (improvement) {
                    improvementText = String(improvement);
                  }

                  if (improvementText && improvementText !== 'null' && improvementText !== 'undefined' && improvementText.trim().length > 0) {
                    imageContent += `${idx + 1}. ${improvementText}\n`;
                  }
                });
                imageContent += `\n`;
              }

              imageContent += `**💬 Your Original Prompt:**\n\`\`\`\n${imageData.originalPrompt || msg.content}\n\`\`\`\n\n`;
              imageContent += `*💡 Tip: Use the revised prompt for better results next time!*`;
            }

            const imageMsg = {
              role: "assistant",
              content: sanitizeMessageContent(imageContent),
              imageUrl: finalImageUrl, // Store image URL separately for direct display
              timestamp: new Date().toISOString(),
            };
            setMessages([...updatedMessages, imageMsg]);
            setIsProcessing(false);
            setProcessingMessage("");
            return;
          } catch (error) {
            console.error(`❌ Image generation error (attempt ${attempts}):`, error);
            if (attempts >= maxAttempts) {
              const errorMsg = {
                role: "assistant",
                content: sanitizeMessageContent(`⚠️ Error generating image: ${error.message || "Unknown error"}`),
                timestamp: new Date().toISOString(),
              };
              setMessages([...updatedMessages, errorMsg]);
              setIsProcessing(false);
              setProcessingMessage("");
              return;
            }
            // Retry delay
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      // 🎬 Check for video generation request
      if (isVideoGenerationRequest(msg.content)) {
        console.log("🎬 Video generation detected for:", msg.content);
        setIsProcessing(false);
        setProcessingMessage("");
        const errorMsg = {
          role: "assistant",
          content: sanitizeMessageContent("I apologize, but we are unable to do video generation in this version. Thank you for your understanding! 😊"),
          timestamp: new Date().toISOString(),
        };
        setMessages([...updatedMessages, errorMsg]);
        return;
      }

      // More descriptive processing steps for better UX (already set processing state above)
      const steps = [
        "🧠 Analyzing your request...",
        "🔍 Searching for information...",
        "✨ Generating response...",
        "📝 Crafting the perfect answer..."
      ];
      let i = 0;
      setProcessingMessage(steps[i]);
      const interval = setInterval(() => {
        i = (i + 1) % steps.length;
        setProcessingMessage(steps[i]);
      }, 1500); // Slightly longer interval for better readability

      try {
        // 🔄 Call backend API with retry and cache-busting
        let attempts = 0;
        const maxAttempts = 2;
        let success = false;
        let aiReply = "";
        let data = null;

        while (attempts < maxAttempts && !success) {
          attempts++;
          const token = getAuthToken();
          if (!token) {
            throw new Error("Authentication required. Please log in again.");
          }

          // Build enhanced prompt with file information if files are attached
          let enhancedPrompt = msg.content;
          if (uploadedFiles && uploadedFiles.length > 0) {
            const nonImageFiles = uploadedFiles.filter(f => !f.type || !f.type.startsWith('image/'));
            if (nonImageFiles.length > 0) {
              const fileDetails = nonImageFiles.map(f => `${f.name || "file"} (${f.type || "unknown type"})`).join(", ");
              enhancedPrompt = `[Files attached: ${fileDetails} - Please analyze these files if possible]\n\n${msg.content}`;
            }
          }

          // Add cache busting to prevent CloudFront from serving index.html
          const fetchUrl = `${API_BASE_URL}/api/chat`;

          const response = await fetch(fetchUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-Requested-With": "XMLHttpRequest",
              "Accept": "application/json",
            },
            body: JSON.stringify({
              projectId: currentProject || null,
              chatId: isTemporaryChat ? null : activeChatId, // ✅ FIX: never send temporary chat id
              message: enhancedPrompt,
              model: selectedModel || "gpt-4o-mini",
              files: uploadedFiles,
            }),
          });

          console.log("🧠 Chat API response status:", response.status);

          // Get response text first to check if it's HTML or JSON
          const responseText = await response.text();
          const contentType = response.headers.get("content-type") || "";
          const isHtml =
            contentType.includes("text/html") ||
            responseText.trim().toLowerCase().startsWith("<!doctype") ||
            responseText.trim().toLowerCase().startsWith("<html") ||
            responseText.trim().toLowerCase().startsWith("<!doctype html");

          if (isHtml) {
            // If HTML and status is 403, assume it's a limit error
            if (response.status === 403) {
              console.error("❌ Chat API returned HTML with 403 status. This might be a limit error.");
              const errorMsg = {
                role: "assistant",
                content: sanitizeMessageContent("I apologize, but your daily prompt limit is over for today. We politely suggest upgrading your plan to continue or checking back tomorrow when your limit resets. Thank you! 😊"),
                timestamp: new Date().toISOString(),
              };
              setMessages([...updatedMessages, errorMsg]);
              setIsProcessing(false);
              setProcessingMessage("");
              clearInterval(interval);
              return;
            }

            // If misrouted (HTML response), retry once after a short delay
            if (attempts < maxAttempts) {
              console.warn(`⚠️ Chat API misrouted (attempt ${attempts}). Retrying with cache-busting...`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              continue;
            }

            // For other HTML responses (even 200 OK) after retries
            console.error("❌ Chat API returned HTML instead of JSON. API endpoint may not be accessible.");
            console.error("Status:", response.status);
            console.error("URL:", response.url);
            console.error("Response preview:", responseText.substring(0, 500));

            setIsProcessing(false);
            setProcessingMessage("");
            clearInterval(interval);

            if (responseText.includes("Login") || responseText.includes("Sign In") || response.status === 401) {
              const loginErrorMsg = {
                role: "assistant",
                content: sanitizeMessageContent("⚠️ **Session Expired**\n\nYour session may have expired or you were redirected to the login page. Please log in again to continue."),
                timestamp: new Date().toISOString(),
              };
              setMessages([...updatedMessages, loginErrorMsg]);
              setIsProcessing(false);
              setProcessingMessage("");
              clearInterval(interval);
              return;
            }

            let detailedError = `⚠️ **Chat service is temporarily unavailable.**\n\nOur servers may be experiencing issues or the request was misrouted. (Status: ${response.status})\n\n`;
            if (responseText.includes("<!doctype html>") || responseText.includes("<html")) {
              detailedError += "Detected misrouting: The API request reached the frontend instead of the backend. This can happen during deployment propagation or if the URL mapping in CloudFront is incorrect.\n\n**Action required:** Please try refreshing the page or clear your browser cache. If you are using a custom domain, ensure it is properly pointed to the backend.";
            } else {
              detailedError += "Please try again in a few moments.";
            }

            const errorMsg = {
              role: "assistant",
              content: sanitizeMessageContent(detailedError),
              timestamp: new Date().toISOString(),
            };
            setMessages([...updatedMessages, errorMsg]);
            return;
          }

          if (!response.ok) {
            let errorData = {};

            // Try to parse as JSON
            try {
              errorData = JSON.parse(responseText);
            } catch (e) {
              console.error("Failed to parse error response as JSON:", responseText.substring(0, 200));
              // If status is 403 and we can't parse, assume it's a limit error
              if (response.status === 403) {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent("Your daily prompt limit has been reached for today. Your limits will automatically reset tomorrow at midnight."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                clearInterval(interval);
                return;
              }
              errorData = {
                error: "SERVER_ERROR",
                message: `Server returned ${response.status} error. Please try again later.`
              };
            }

            // 🔍 Handle "Chat not found" (404) by cleaning up local state and retrying as new chat
            if (response.status === 404 && (errorData.error === "Chat not found" || errorData.message === "Chat not found")) {
              console.warn(`⚠️ Chat ID ${activeChatId} not found on backend. Cleaning up and retrying as new chat...`);

              // Remove invalid chat from local state
              const cleanedChats = chats.filter(c => c.id !== activeChatId);
              setChats(cleanedChats);
              persist("exivis_chats", cleanedChats);

              // If we still have attempts left, retry without a chatId
              if (attempts < maxAttempts) {
                activeChatId = null; // Next attempt will be treated as a new chat
                isTemporaryChat = true;
                await new Promise(resolve => setTimeout(resolve, 500));
                continue;
              }
            }

            if (response.status === 401 || response.status === 403 || response.status === 503 || response.status === 500) {
              // Extract detailed error message if available
              let errorData = {};
              try {
                errorData = JSON.parse(responseText);
              } catch (e) {
                // Not JSON, already handled by responseText check above
              }

              // Handle limit reached errors with user-friendly messages
              if (errorData.error === "DAILY_LIMIT_REACHED" || errorData.error === "FREE_LIMIT_REACHED") {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent(errorData.message || "Your daily prompt limit has been reached for today. Your limits will automatically reset tomorrow at midnight."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                clearInterval(interval);
                return;
              }

              if (errorData.error === "PLAN_NOT_SUPPORTED") {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent(errorData.message || "This feature is not available for your plan. Please upgrade to access this feature."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                clearInterval(interval);
                return;
              }

              // NEW: Handle 403 specifically if no matching error code but looks like a limit error
              if ((response.status === 403 || response.status === 500) && (errorData.message?.toLowerCase().includes("limit") || errorData.message?.toLowerCase().includes("plan") || errorData.error?.includes("LIMIT"))) {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent(errorData.message || "You have reached a limit or your plan does not support this feature."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                clearInterval(interval);
                return;
              }

              // Handle 503 DATABASE_ERROR
              if (response.status === 503 || errorData.error === "DATABASE_ERROR") {
                const errorMsg = {
                  role: "assistant",
                  content: sanitizeMessageContent(errorData.message || "⚠️ **Service Temporarily Unavailable**\n\nThe database is currently busy. Please try again in a few moments."),
                  timestamp: new Date().toISOString(),
                };
                setMessages([...updatedMessages, errorMsg]);
                setIsProcessing(false);
                setProcessingMessage("");
                clearInterval(interval);
                return;
              }

              if (response.status === 401 || response.status === 403) {
                throw new Error("Authentication failed. Please log in again.");
              }
            }
            throw new Error(errorData.error || errorData.message || "Failed to get AI response");
          }

          // Parse response as JSON
          try {
            data = JSON.parse(responseText);
            aiReply = data.reply || "";
            success = true;
          } catch (e) {
            console.error("Failed to parse response as JSON:", responseText.substring(0, 200));
            throw new Error("Server returned invalid response. Please try again later.");
          }
        }

        // Handle empty chat deletion from backend FIRST
        if (data.chatId === null || data.message === "Chat was not saved because it has no messages.") {
          // Backend deleted empty chat - remove from local state
          console.log("🗑️ Backend deleted empty chat. Removing from local state.");
          const chatIndex = updatedChats.findIndex(c => c.id === activeChatId);
          if (chatIndex !== -1) {
            updatedChats.splice(chatIndex, 1);
            setChats(updatedChats);
            setCurrentChat(null);
            setMessages([]);
            persist("exivis_chats", updatedChats);
          }
          setIsProcessing(false);
          setProcessingMessage("");
          return;
        }

        // Convert backend messages format to frontend format FIRST
        // Ensure content is always a string to prevent React error #130
        // This must be defined before it's used below
        const backendMessages = (data.messages || []).map((m) => {
          // Double-sanitize to prevent any objects from getting through
          let safeContent = m.content;
          if (safeContent === null || safeContent === undefined) {
            safeContent = "";
          } else if (typeof safeContent !== "string") {
            safeContent = sanitizeMessageContent(safeContent);
          }
          // Final check - ensure it's a string
          if (typeof safeContent !== "string") {
            safeContent = String(safeContent || "");
          }
          return {
            role: m.role || "user",
            content: safeContent,
            timestamp: m.created_at || m.timestamp || new Date().toISOString(),
          };
        });

        // Update chat ID if this was a new chat or temporary chat
        if (data.chatId) {
          // If we had a temporary chat, replace it with the real backend chat
          const currentChatObj = updatedChats.find(c => c.id === activeChatId);
          if (currentChatObj && currentChatObj.isTemporary) {
            // Remove temporary chat and add real chat
            const tempChatIndex = updatedChats.findIndex(c => c.id === activeChatId);
            if (tempChatIndex !== -1) {
              updatedChats.splice(tempChatIndex, 1);
            }

            // Add real chat from backend ONLY if it has messages
            if (backendMessages.length > 0) {
              const realChat = {
                id: data.chatId,
                title: data.titleUpdated && backendMessages.length > 0
                  ? generateChatTitle(backendMessages.find(m => m.role === "user")?.content || "New Chat")
                  : "New Chat",
                messages: backendMessages,
                projectId: currentProject,
                createdAt: new Date().toISOString(),
                is_pinned: false,
                is_archived: false,
              };
              updatedChats.push(realChat);
              activeChatId = data.chatId;
              setCurrentChat(data.chatId);
              // CRITICAL: Also update messages state to match backendMessages
              setMessages(backendMessages);
            } else {
              // No messages - don't add the chat
              console.log("🗑️ Chat has no messages. Not adding to local state.");
              setCurrentChat(null);
              setMessages([]);
            }
          } else if (data.chatId !== activeChatId) {
            // Update existing chat ID if it changed
            activeChatId = data.chatId;
            setCurrentChat(data.chatId);
          }
        }

        // Update title if it was updated
        if (data.titleUpdated && backendMessages.length > 0) {
          const firstUserMessage = backendMessages.find(m => m.role === "user");
          if (firstUserMessage) {
            const newTitle = generateChatTitle(firstUserMessage.content);
            updatedChats = updatedChats.map((chat) =>
              chat.id === activeChatId ? { ...chat, title: newTitle } : chat
            );
          }
        }

        // Ensure we have messages - if backendMessages is empty but we have a reply, add it
        if (backendMessages.length === 0 && data.reply) {
          // Backend returned a reply but no messages - this can happen on first message
          // Add the user message and assistant reply
          const userMsg = updatedMessages.find(m => m.role === "user");
          if (userMsg) {
            backendMessages.push(userMsg);
            backendMessages.push({
              role: "assistant",
              content: sanitizeMessageContent(data.reply),
              timestamp: new Date().toISOString(),
            });
          }
        }

        // Only update if we have messages (don't save empty chats)
        if (backendMessages.length > 0) {
          setMessages(backendMessages);
          updatedChats = updatedChats.map((chat) =>
            chat.id === activeChatId ? { ...chat, messages: backendMessages, isTemporary: false } : chat
          );
          setChats(updatedChats);
          persist("exivis_chats", updatedChats);
        } else {
          // No messages - chat was empty, remove it
          console.log("🗑️ Chat has no messages. Removing from local state.");
          const chatIndex = updatedChats.findIndex(c => c.id === activeChatId);
          if (chatIndex !== -1) {
            updatedChats.splice(chatIndex, 1);
            setChats(updatedChats);
            setCurrentChat(null);
            setMessages([]);
            persist("exivis_chats", updatedChats);
          }
        }

        // Clear interval and processing state on success
        clearInterval(interval);
        setIsProcessing(false);
        setProcessingMessage("");
      } catch (error) {
        console.error("❌ Chat API error:", error);

        // Stop processing immediately
        if (interval) clearInterval(interval);
        setIsProcessing(false);
        setProcessingMessage("");

        // Check if error is due to HTML response (SyntaxError from JSON.parse)
        let errorMsg = error.message || "Unknown error";

        // Check if it's a 403 error (likely a limit error)
        if (error.message && (error.message.includes("403") || error.message.includes("limit") || error.message.includes("Limit"))) {
          errorMsg = "Your daily prompt limit has been reached for today. Your limits will automatically reset tomorrow at midnight.";
        } else if (error instanceof SyntaxError && error.message.includes("Unexpected token")) {
          errorMsg = "Chat service is temporarily unavailable. The server may be experiencing issues. Please try again later or contact support.";
        } else if (error.message && error.message.includes("HTML")) {
          errorMsg = error.message;
        } else if (error.message && error.message.includes("temporarily unavailable")) {
          errorMsg = error.message;
        }

        // If this was a temporary chat and the first message failed, clean it up
        const currentChatObj = chats.find(c => c.id === activeChatId);
        if (currentChatObj && currentChatObj.isTemporary) {
          console.log("🗑️ First message failed for temporary chat. Cleaning up.");
          const chatIndex = chats.findIndex(c => c.id === activeChatId);
          if (chatIndex !== -1) {
            const cleanedChats = chats.filter(c => c.id !== activeChatId);
            setChats(cleanedChats);
            setCurrentChat(null);
            setMessages([]);
            persist("exivis_chats", cleanedChats);
            localStorage.removeItem("exivis_currentChat");
          }
          // Don't add error message for temporary chats that failed - just clean up
          return;
        }

        // Only add error message if we have an active chat and haven't already shown this error
        // Prevent duplicate error messages
        const lastMessage = updatedMessages[updatedMessages.length - 1];
        const isDuplicateError = lastMessage &&
          lastMessage.role === "assistant" &&
          lastMessage.content &&
          lastMessage.content.includes(errorMsg);

        if (!isDuplicateError && activeChatId) {
          const errorMessage = {
            role: "assistant",
            content: sanitizeMessageContent(`⚠️ Error: ${sanitizeMessageContent(errorMsg)}`),
            timestamp: new Date().toISOString(),
          };

          const finalMessages = [...updatedMessages, errorMessage];
          setMessages(finalMessages);

          updatedChats = chats.map((chat) =>
            chat.id === activeChatId ? { ...chat, messages: finalMessages } : chat
          );
          setChats(updatedChats);
          persist("exivis_chats", updatedChats);
        }
      }
    }
  };

  // ✏️ Edit Message
  const editMessage = async (messageIndex, newContent) => {
    try {
      if (messageIndex < 0 || messageIndex >= messages.length) {
        console.error("Invalid message index:", messageIndex);
        return;
      }

      const messageToEdit = messages[messageIndex];
      if (messageToEdit.role !== "user") {
        console.error("Can only edit user messages");
        return; // Only allow editing user messages
      }

      // Update the message locally first
      const updatedMessages = [...messages];
      updatedMessages[messageIndex] = {
        ...messageToEdit,
        content: newContent,
      };
      setMessages(updatedMessages);

      // Update in chats array
      const activeChatId = currentChat;
      if (activeChatId) {
        const updatedChats = chats.map((chat) =>
          chat.id === activeChatId ? { ...chat, messages: updatedMessages } : chat
        );
        setChats(updatedChats);
        persist("exivis_chats", updatedChats);

        // Remove all messages after the edited one (to resend from that point)
        const messagesToKeep = updatedMessages.slice(0, messageIndex + 1);
        setMessages(messagesToKeep);

        // Resend the edited message to get new AI response
        await addMessage({
          role: "user",
          content: newContent,
        });
      }
    } catch (error) {
      console.error("Error editing message:", error);
    }
  };

  // 🔄 Regenerate last assistant response
  const regenerateLastResponse = async () => {
    try {
      if (messages.length === 0) {
        console.warn("No messages to regenerate");
        return;
      }

      // Find the last user message
      let lastUserMessageIndex = -1;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          lastUserMessageIndex = i;
          break;
        }
      }

      if (lastUserMessageIndex === -1) {
        console.warn("No user message found to regenerate from");
        return;
      }

      // Remove all assistant messages after the last user message
      const updatedMessages = messages.slice(0, lastUserMessageIndex + 1);
      setMessages(updatedMessages);

      // Update in chats array
      const activeChatId = currentChat;
      if (activeChatId) {
        const updatedChats = chats.map((chat) =>
          chat.id === activeChatId ? { ...chat, messages: updatedMessages } : chat
        );
        setChats(updatedChats);
        persist("exivis_chats", updatedChats);
      }

      // Get the last user message and resend it (this will trigger AI response)
      const lastUserMessage = messages[lastUserMessageIndex];

      // Temporarily disable isProcessing check for regeneration
      setIsProcessing(false);

      // Resend the last user message to get new AI response
      await addMessage({
        role: "user",
        content: lastUserMessage.content,
        files: lastUserMessage.files || [],
      });
    } catch (error) {
      console.error("Error regenerating response:", error);
      setIsProcessing(false);
      setProcessingMessage("");
    }
  };

  // 🔁 Switch Chat
  const switchChat = async (chatId) => {
    const chat = chats.find((c) => c.id === chatId);
    if (chat) {
      setCurrentChat(chat.id);
      setCurrentProject(chat.projectId);

      // If messages are already loaded, use them
      if (chat.messages && chat.messages.length > 0) {
        setMessages(chat.messages);
      } else {
        // Otherwise, fetch messages from backend
        try {
          const token = getAuthToken();
          if (token) {
            const messagesResponse = await fetch(`${API_BASE_URL}/api/chat/chats/${chatId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (messagesResponse.ok) {
              const chatData = await messagesResponse.json();
              const messages = (chatData.messages || []).map((m) => ({
                role: m.role,
                content: m.content,
                timestamp: m.created_at || new Date().toISOString(),
                file_url: m.file_url,
                file_type: m.file_type,
                file_name: m.file_name,
              }));

              setMessages(messages);

              // Update chat in state with messages
              const updatedChats = chats.map((c) =>
                c.id === chatId ? { ...c, messages } : c
              );
              setChats(updatedChats);
              persist("exivis_chats", updatedChats);
            } else {
              setMessages([]);
            }
          } else {
            setMessages(chat.messages || []);
          }
        } catch (error) {
          console.error("Error fetching messages for chat:", error);
          setMessages(chat.messages || []);
        }
      }

      // Save current chat to localStorage for persistence on refresh
      localStorage.setItem("exivis_currentChat", String(chat.id));
      localStorage.setItem("exivis_currentProject", String(chat.projectId || ""));
    }
  };

  // ✏️ Update Chat Title
  const updateChatTitle = async (chatId, newTitle) => {
    if (!newTitle || !newTitle.trim()) return;

    // Update backend
    try {
      await fetch(`${API_BASE_URL}/api/chat/chats/${chatId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
    } catch (error) {
      console.error("Error updating chat title:", error);
    }

    // Update local state
    const updated = chats.map((c) =>
      c.id === chatId ? { ...c, title: newTitle.trim() } : c
    );
    setChats(updated);
    persist("exivis_chats", updated);
  };

  // 🗑 Delete Chat
  const deleteChat = async (chatId) => {
    // Delete from backend first
    try {
      await fetch(`${API_BASE_URL}/api/chat/chats/${chatId}`, {
        method: "DELETE",
        headers: {
          ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
        },
      });
    } catch (error) {
      console.error("Error deleting chat from backend:", error);
    }

    // Update local state
    const updated = chats.filter((c) => c.id !== chatId);
    setChats(updated);
    persist("exivis_chats", updated);
    if (currentChat === chatId) {
      setCurrentChat(null);
      setMessages([]);
    }
  };

  // 📌 Toggle Pin Chat
  const togglePinChat = async (chatId) => {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) return;

    const updatedChats = chats.map((c) =>
      c.id === chatId ? { ...c, is_pinned: !c.is_pinned } : c
    );
    setChats(updatedChats);
    persist("exivis_chats", updatedChats);

    // Update backend
    try {
      await fetch(`${API_BASE_URL}/api/chat/chats/${chatId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
        },
        body: JSON.stringify({ is_pinned: !chat.is_pinned }),
      });
    } catch (error) {
      console.error("Error updating pin status:", error);
    }
  };

  // 📦 Toggle Archive Chat
  const toggleArchiveChat = async (chatId) => {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) return;

    const updatedChats = chats.map((c) =>
      c.id === chatId ? { ...c, is_archived: !c.is_archived } : c
    );
    setChats(updatedChats);
    persist("exivis_chats", updatedChats);

    // Update backend
    try {
      await fetch(`${API_BASE_URL}/api/chat/chats/${chatId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
        },
        body: JSON.stringify({ is_archived: !chat.is_archived }),
      });
    } catch (error) {
      console.error("Error updating archive status:", error);
    }
  };

  // 📂 Filter Chats by Project (exclude archived)
  const getChatsByProject = (projectId) => {
    let filtered = projectId ? chats.filter((c) => c.projectId === projectId) : chats;
    filtered = filtered.filter((c) => !c.is_archived);
    // Sort: pinned first, then by date
    return filtered.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  };

  // 🎨 Image Generation Detection
  const isImageGenerationRequest = (text) => {
    if (!text) return false;
    const lowerText = text.toLowerCase().trim();

    // Check for explicit "generate", "create", etc.
    const generateImagePattern = /^(generate|create|make|draw|paint|show|give\s+me)\s+(a|an|the)?\s*(image|picture|photo|illustration|visual|artwork|graphic|design|portrait|landscape|sketch|drawing)/i;

    // Catch "image of...", "picture of...", etc.
    const ofPattern = /^(image|picture|photo|illustration|artwork|sketch)\s+of\s+/i;

    // NEW: Catch cases where the user just says "generate image" or "create a picture" anywhere
    const anywherePattern = /(generate|create|make|draw)\s+(a|an|the)?\s*(image|picture|photo|illustration|artwork)/i;

    return generateImagePattern.test(lowerText) || ofPattern.test(lowerText) || anywherePattern.test(lowerText);
  };

  // 🎬 Video Generation Detection
  const isVideoGenerationRequest = (text) => {
    if (!text) return false;
    const lowerText = text.toLowerCase().trim();
    const pattern = /^(generate|create|make|draw)\s+(a|an|the)?\s*(video|clip|reel|short)/i;
    return pattern.test(lowerText);
  };

  // 🔄 Sync messages when chat changes
  useEffect(() => {
    if (currentChat) {
      const chat = chats.find((c) => c.id === currentChat);
      if (chat) setMessages(chat.messages || []);
    }
  }, [currentChat, chats]);

  // 🆕 Fresh start on login - clear old data and sync from backend
  useEffect(() => {
    // Sync from backend
    const syncFromBackend = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          // Clear localStorage if no token
          console.log("🔒 No auth token, clearing chat data");
          localStorage.removeItem("exivis_currentChat");
          localStorage.removeItem("exivis_chats");
          localStorage.removeItem("exivis_messages");
          setChats([]);
          setMessages([]);
          setCurrentChat(null);
          return;
        }

        console.log("🔄 Starting chat sync from backend...");

        // Fetch chats from backend
        const chatsResponse = await fetch(`${API_BASE_URL}/api/chat/chats`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Requested-With": "XMLHttpRequest",
            "Accept": "application/json",
          },
        });

        if (chatsResponse.ok) {
          // Check if response is HTML
          const responseText = await chatsResponse.text();
          const isHtml = responseText.trim().toLowerCase().startsWith("<!doctype") || responseText.trim().toLowerCase().startsWith("<html");

          if (isHtml) {
            console.error("Chat fetch API: Server returned HTML instead of JSON. API may not be available.");
            return; // Silently fail - will use local storage
          }

          let backendChats;
          try {
            backendChats = JSON.parse(responseText);
          } catch (e) {
            console.error("Failed to parse chats response as JSON:", responseText.substring(0, 200));
            return; // Silently fail - will use local storage
          }
          console.log("📥 Fetched chats from backend:", backendChats?.length || 0, "chats");

          // Fetch messages for each chat
          const chatsWithMessages = await Promise.all(
            (backendChats || []).map(async (chat) => {
              try {
                const messagesResponse = await fetch(`${API_BASE_URL}/api/chat/chats/${chat.id}`, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                  },
                });

                if (messagesResponse.ok) {
                  // Check if response is HTML
                  const messagesResponseText = await messagesResponse.text();
                  const isMessagesHtml = messagesResponseText.trim().toLowerCase().startsWith("<!doctype") || messagesResponseText.trim().toLowerCase().startsWith("<html");

                  if (isMessagesHtml) {
                    console.warn(`Messages fetch for chat ${chat.id}: Server returned HTML instead of JSON. Skipping.`);
                    // Return chat without messages
                    return {
                      id: chat.id,
                      title: chat.title || "New Chat",
                      projectId: chat.project_id,
                      messages: [],
                      createdAt: chat.created_at || new Date().toISOString(),
                      is_pinned: chat.is_pinned || false,
                      is_archived: chat.is_archived || false,
                    };
                  }

                  let chatData;
                  try {
                    chatData = JSON.parse(messagesResponseText);
                  } catch (e) {
                    console.error("Failed to parse messages response as JSON:", messagesResponseText.substring(0, 200));
                    // Return chat without messages
                    return {
                      id: chat.id,
                      title: chat.title || "New Chat",
                      projectId: chat.project_id,
                      messages: [],
                      createdAt: chat.created_at || new Date().toISOString(),
                      is_pinned: chat.is_pinned || false,
                      is_archived: chat.is_archived || false,
                    };
                  }
                  // Convert backend message format to frontend format
                  const messages = (chatData.messages || []).map((m) => ({
                    role: m.role,
                    content: m.content,
                    timestamp: m.created_at || new Date().toISOString(),
                    file_url: m.file_url,
                    file_type: m.file_type,
                    file_name: m.file_name,
                  }));

                  return {
                    id: chat.id,
                    title: chat.title || "New Chat",
                    projectId: chat.project_id,
                    messages: messages,
                    createdAt: chat.created_at || new Date().toISOString(),
                    is_pinned: chat.is_pinned || false,
                    is_archived: chat.is_archived || false,
                  };
                }

                // Fallback if messages fetch fails
                return {
                  id: chat.id,
                  title: chat.title || "New Chat",
                  projectId: chat.project_id,
                  messages: [],
                  createdAt: chat.created_at || new Date().toISOString(),
                  is_pinned: chat.is_pinned || false,
                  is_archived: chat.is_archived || false,
                };
              } catch (error) {
                console.error(`Error fetching messages for chat ${chat.id}:`, error);
                return {
                  id: chat.id,
                  title: chat.title || "New Chat",
                  projectId: chat.project_id,
                  messages: [],
                  createdAt: chat.created_at || new Date().toISOString(),
                  is_pinned: chat.is_pinned || false,
                  is_archived: chat.is_archived || false,
                };
              }
            })
          );

          console.log("✅ Synced chats with messages:", chatsWithMessages?.length || 0, "chats");
          setChats(chatsWithMessages);
          persist("exivis_chats", chatsWithMessages);

          // Restore current chat if it exists
          const savedChatId = localStorage.getItem("exivis_currentChat");
          if (savedChatId && chatsWithMessages.find((c) => c.id === Number(savedChatId))) {
            setCurrentChat(Number(savedChatId));
          }
        } else {
          console.error("❌ Failed to fetch chats:", chatsResponse.status, chatsResponse.statusText);
          const errorText = await chatsResponse.text().catch(() => "");
          console.error("Error response:", errorText);

          if (chatsResponse.status === 502) {
            // Bad Gateway - Service might be restarting or overloaded
            // Fallback to localStorage if available
            const localChats = localStorage.getItem("exivis_chats");
            if (localChats) {
              console.log("📂 502 error: Falling back to local chat history");
              setChats(JSON.parse(localChats));
            }
          } else {
            // If fetch fails for other reasons, clear localStorage to prevent stale data
            localStorage.removeItem("exivis_currentChat");
            localStorage.removeItem("exivis_chats");
            localStorage.removeItem("exivis_messages");
          }
        }
      } catch (error) {
        console.error("Error syncing from backend:", error);
        // Clear localStorage on error
        localStorage.removeItem("exivis_currentChat");
        localStorage.removeItem("exivis_chats");
        localStorage.removeItem("exivis_messages");
      }
    };

    syncFromBackend();
  }, []);

  // 🔒 Clear chat data when token changes (user switches)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // If auth_token is removed or changed, clear chat data
      if (e.key === "auth_token") {
        if (!e.newValue) {
          // Token removed - clear all chat data
          console.log("🔒 Auth token removed, clearing chat data");
          localStorage.removeItem("exivis_currentChat");
          localStorage.removeItem("exivis_chats");
          localStorage.removeItem("exivis_messages");
          localStorage.removeItem("exivis_projects");
          localStorage.removeItem("exivis_currentProject");
          setChats([]);
          setMessages([]);
          setCurrentChat(null);
          setCurrentProject(null);
        } else if (e.oldValue && e.newValue !== e.oldValue) {
          // Token changed - clear old user's data and sync new user's data
          console.log("🔒 Auth token changed, clearing old chat data and syncing new user");
          localStorage.removeItem("exivis_currentChat");
          localStorage.removeItem("exivis_chats");
          localStorage.removeItem("exivis_messages");
          localStorage.removeItem("exivis_projects");
          localStorage.removeItem("exivis_currentProject");
          setChats([]);
          setMessages([]);
          setCurrentChat(null);
          setCurrentProject(null);
          // Sync new user's data
          setTimeout(() => {
            const token = getAuthToken();
            if (token) {
              // Trigger sync
              const syncFromBackend = async () => {
                try {
                  const chatsResponse = await fetch(`${API_BASE_URL}/api/chat/chats`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (chatsResponse.ok) {
                    const responseText = await chatsResponse.text();
                    const isHtml = responseText.trim().toLowerCase().startsWith("<!doctype") || responseText.trim().toLowerCase().startsWith("<html");
                    if (!isHtml) {
                      const backendChats = JSON.parse(responseText);
                      // Fetch messages for each chat and update state
                      const chatsWithMessages = await Promise.all(
                        (backendChats || []).map(async (chat) => {
                          try {
                            const messagesResponse = await fetch(`${API_BASE_URL}/api/chat/chats/${chat.id}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (messagesResponse.ok) {
                              const messagesResponseText = await messagesResponse.text();
                              const isMessagesHtml = messagesResponseText.trim().toLowerCase().startsWith("<!doctype") || messagesResponseText.trim().toLowerCase().startsWith("<html");
                              if (!isMessagesHtml) {
                                const chatData = JSON.parse(messagesResponseText);
                                const messages = (chatData.messages || []).map((m) => ({
                                  role: m.role,
                                  content: m.content,
                                  timestamp: m.created_at || new Date().toISOString(),
                                  file_url: m.file_url,
                                  file_type: m.file_type,
                                  file_name: m.file_name,
                                }));
                                return {
                                  id: chat.id,
                                  title: chat.title || "New Chat",
                                  projectId: chat.project_id,
                                  messages: messages,
                                  createdAt: chat.created_at || new Date().toISOString(),
                                  is_pinned: chat.is_pinned || false,
                                  is_archived: chat.is_archived || false,
                                };
                              }
                            }
                            return {
                              id: chat.id,
                              title: chat.title || "New Chat",
                              projectId: chat.project_id,
                              messages: [],
                              createdAt: chat.created_at || new Date().toISOString(),
                              is_pinned: chat.is_pinned || false,
                              is_archived: chat.is_archived || false,
                            };
                          } catch (err) {
                            console.error(`Error fetching messages for chat ${chat.id}:`, err);
                            return {
                              id: chat.id,
                              title: chat.title || "New Chat",
                              projectId: chat.project_id,
                              messages: [],
                              createdAt: chat.created_at || new Date().toISOString(),
                              is_pinned: chat.is_pinned || false,
                              is_archived: chat.is_archived || false,
                            };
                          }
                        })
                      );
                      setChats(chatsWithMessages);
                      persist("exivis_chats", chatsWithMessages);
                    }
                  }
                } catch (error) {
                  console.error("Error syncing after token change:", error);
                }
              };
              syncFromBackend();
            }
          }, 100);
        }
      }
    };

    // Listen for storage changes (token changes)
    window.addEventListener("storage", handleStorageChange);

    // Also check token on mount and when component updates
    const checkToken = () => {
      const token = getAuthToken();
      if (!token) {
        // No token - ensure chat data is cleared
        localStorage.removeItem("exivis_currentChat");
        localStorage.removeItem("exivis_chats");
        localStorage.removeItem("exivis_messages");
        setChats([]);
        setMessages([]);
        setCurrentChat(null);
      }
    };

    // Check immediately
    checkToken();

    // Set up interval to check token changes (since storage event doesn't fire in same window)
    const tokenCheckInterval = setInterval(() => {
      const currentToken = getAuthToken();
      const lastToken = window._lastAuthToken;
      if (currentToken !== lastToken) {
        window._lastAuthToken = currentToken;
        if (!currentToken && lastToken) {
          // Token was removed
          handleStorageChange({ key: "auth_token", newValue: null, oldValue: lastToken });
        } else if (currentToken && lastToken && currentToken !== lastToken) {
          // Token changed
          handleStorageChange({ key: "auth_token", newValue: currentToken, oldValue: lastToken });
        }
      }
    }, 500);

    // Store initial token
    window._lastAuthToken = getAuthToken();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(tokenCheckInterval);
    };
  }, []);

  // Also sync when token becomes available (listen to storage events)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "auth_token" && e.newValue && chats.length === 0) {
        console.log("🔄 Auth token detected, syncing chats...");
        const syncFromBackend = async () => {
          try {
            const token = getAuthToken();
            if (!token) return;

            const chatsResponse = await fetch(`${API_BASE_URL}/api/chat/chats`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            if (chatsResponse.ok) {
              const backendChats = await chatsResponse.json();
              if (backendChats && backendChats.length > 0) {
                console.log("📥 Re-syncing chats after login:", backendChats.length);
                // Re-run the full sync logic
                window.location.reload(); // Simple approach: reload to trigger full sync
              }
            }
          } catch (error) {
            console.error("Error re-syncing:", error);
          }
        };
        syncFromBackend();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [chats.length]);

  return (
    <ChatContext.Provider
      value={{
        chats,
        messages,
        projects,
        currentChat,
        currentProject,
        startNewChat,
        addMessage,
        editMessage,
        regenerateLastResponse,
        switchChat,
        deleteChat,
        updateChatTitle,
        createProject,
        getChatsByProject,
        setCurrentProject,
        togglePinChat,
      toggleArchiveChat,
      selectedModel,
      setSelectedModel,
        selectedFiles,
        setSelectedFiles,
        // 👇 new states for spinner
        isProcessing,
        processingMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
